<template>
  <div class="car-reservation-details">
    <div v-if="car.LotRelocations" class="row">
      <div class="col-lg-12">
        <template v-for="relo in car.LotRelocations">
          <div v-if="relo.permanent" class="callout callout-warning">
            <p>
              This car will be relocated to a different parking bay between
              <strong>{{ new Date(relo.startDate).getTime() - relo.buffer * 60000 | date_format car.Lot.timezone }}</strong>
              and <strong>{{ new Date(relo.endDate).getTime() + relo.buffer * 60000 | date_format car.Lot.timezone }}</strong>.
            </p>
            <p>
              From <strong>{{ new Date(relo.endDate).getTime() + relo.buffer * 60000 | date_format car.Lot.timezone }}</strong>,
              this car will be available to book at
              <a :href="'https://www.google.com/maps/dir/\'' +
              relo.OriginalLot.latitude + ',' + relo.OriginalLot.longitude +
              '\'/' + relo.Lot.latitude + ',' + relo.Lot.longitude +
              '/data=!3m1!4b1!4m7!4m6!1m3!2m2!1d145.0580063!2d-37.7004365!1m0!3e2'"
              target="_blank"><u>{{ relo.Lot.address }}</u></a>.
            </p>
            <p>
              This car was originally located at: <a v-bind:href="'https://www.google.com/maps?q='+ relo.OriginalLot.latitude +','+ relo.OriginalLot.longitude" target="_blank"><u>{{ relo.OriginalLot.address }}</u></a>.
            </p>
          </div>
          <div v-else class="callout callout-info">
            <p>
              This car will be temporarily located at <a :href="'https://www.google.com/maps/dir/\'' +
              relo.OriginalLot.latitude + ',' + relo.OriginalLot.longitude +
              '\'/' + relo.Lot.latitude + ',' + relo.Lot.longitude +
              '/data=!3m1!4b1!4m7!4m6!1m3!2m2!1d145.0580063!2d-37.7004365!1m0!3e2'"
              target="_blank"><u>{{ relo.Lot.address }}</u></a> between
              <strong>{{ relo.startDate | date_format car.Lot.timezone }}</strong>
              and <strong>{{ relo.endDate | date_format car.Lot.timezone }}</strong>.
            </p>
            <p>
              From <strong>{{ new Date(relo.endDate).getTime() + relo.buffer * 60000 | date_format car.Lot.timezone }}</strong> the car will be available to book at it's original bay: <a v-bind:href="'https://www.google.com/maps?q='+ relo.OriginalLot.latitude +','+ relo.OriginalLot.longitude" target="_blank"><u>{{ relo.OriginalLot.address }}</u></a>.
            </p>
          </div>
        </template>
      </div>
    </div>
    <div class="row">
      <div class="col-sm-5">
        <div class="box box-primary">
          <div class="box-header with-border">
            <h1 class="box-title">Reserve #{{ car.convadisId }} - {{ car.Lot.address }} ({{ car.name }})</h1>
          </div>
          <div class="box-body">
            <vue-form ref="form"
              @updated="refresh()"
              :options="{
                permalink: 'carReservation',
                params: {
                  driver: $route.query.d,
                  start: $route.query.s,
                  end: $route.query.e
                }
              }"
              :is-modal="true">
            </vue-form>
            <car-item
              @reserve="reserve(car)"
              :rates="carRates"
              :object="car"
              :start="startDate"
              :end="endDate"
              :driver="$route.query.d">
            </car-item>
          </div>
        </div>
      </div>
      <div class="col-sm-7">
        <car-availability
          :availability="car.availability"
          :car="car"
          :calendar="false"
          @previous="previousAvailability()"
          @next="nextAvailability()"
          @change="changeAvailability($event)">
        </car-availability>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment-timezone'
import CarAvailability from '@/components/admin/CarAvailability'
import CarItem from '@/components/displays/CarItem'
import VueForm from '@/components/Form'
import { mapGetters } from 'vuex'
import {
  LOAD_CAR,
  LOAD_CAR_AVAILABILITY,
  LOAD_CAR_RATES,
  RESERVE_CAR
} from '@/vuex/types'

export default {
  name: 'CarReservationDetails',
  components: {
    CarAvailability,
    CarItem,
    VueForm
  },
  computed: {
    car () {
      return {
        CarType: {
          CarClass: {}
        },
        ...this.loadedCar
      }
    },
    ...mapGetters([
      'loadedCar',
      'carRates'
    ])
  },
  data () {
    return {
      startDate: moment(this.$route.query.s, 'x').format(),
      endDate: moment(this.$route.query.e, 'x').format()
    }
  },
  async mounted () {
    let startTime = this.$route.query.s || moment().format('x')
    let endTime = this.$route.query.e || null
    this.startDate = startTime
    this.endDate = endTime
    if (this.startDate && this.endDate) {
      await this.$store.dispatch(LOAD_CAR, {
        carUuid: this.$route.params.carId,
        startTime,
        endTime
      })
    } else {
      let noResultsDate = moment('3000-12-01').format('x')
      await this.$store.dispatch(LOAD_CAR, {
        carUuid: this.$route.params.carId,
        startTime: noResultsDate,
        endTime: noResultsDate
      })
      await this.$store.dispatch(LOAD_CAR_AVAILABILITY, {
        carUuid: this.$route.params.carId,
        startTime: moment('3000-12-01').format('x')
      })
    }
    await this.$store.dispatch(LOAD_CAR_RATES)
  },
  methods: {
    async previousAvailability () {
      this.startDate = moment(this.startDate).subtract(1, 'days').format()
      await this.$store.dispatch(LOAD_CAR_AVAILABILITY, {
        startTime: moment(this.startDate).format('x'),
        carUuid: this.$route.params.carId
      })
    },
    async nextAvailability () {
      this.startDate = moment(this.startDate).add(1, 'days').format()
      await this.$store.dispatch(LOAD_CAR_AVAILABILITY, {
        startTime: moment(this.startDate).format('x'),
        carUuid: this.$route.params.carId
      })
    },
    async changeAvailability (date) {
      this.startDate = moment(date).format()
      await this.$store.dispatch(LOAD_CAR_AVAILABILITY, {
        startTime: moment(this.startDate).format('x'),
        carUuid: this.$route.params.carId
      })
    },
    async refresh () {
      let form = this.$refs.form.form
      if (form.isValid && this.startDate && this.endDate) {
        let { value } = form.sections.find((el) => el.name === 'dateTimeRange')
        this.startDate = value.startDate
        this.endDate = value.endDate
        await this.$store.dispatch(LOAD_CAR, {
          carUuid: this.$route.params.carId,
          startTime: moment(value.startDate).format('x'),
          endTime: moment(value.endDate).format('x')
        })
        await this.$store.dispatch(LOAD_CAR_AVAILABILITY, {
          startTime: moment(value.startDate).format('x'),
          carUuid: this.$route.params.carId
        })
      }
    },
    reserve (car) {
      let form = this.$refs.form.form
      let { value } = form.sections.find((el) => el.name === 'AccountUuid')
      this.$store.dispatch(RESERVE_CAR, {
        car,
        driver: value,
        start: this.startDate,
        end: this.endDate
      })
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/admin/CarReservationDetails.vue